import React, { useEffect, useContext, useState, useRef } from "react";
import { BoardNote } from "@/bryntum-lib/models/BoardNote";
import { EntityRecord } from "@/types/entity";
import { Button } from "@/modern/components/ui/button";
import { Label } from "@/modern/components/ui/label";
import { Controller, useForm } from "react-hook-form";
import Combobox from "@/modern/components/ui/combobox";
import { DateHelper } from "@bryntum/schedulerpro";
import BryntumContext from "@/react/context/bryntumContext";
import { AlertTriangle, Loader2, Save, X } from "lucide-react";
import { checkGrantOrder, cn } from "@/modern/utils";
import { Dialog, DialogFooter } from "@/modern/components/ui/dialog-shadcn";
import { SharedModalBody, SharedModalDescription, SharedModal, SharedModalHeader, SharedModalTitle } from "@/modern/components/shared-modal-component";
import { useEntityNotesContext, EntityNotesProvider } from "../../context/EntityNotesContext";
import { NotePreview } from "../NotePreview";
import { NotesMenu } from "../NotesMenu";

// Reusable metadata card component
const MetadataCard = ({ label, value }: { label: string; value: string | React.ReactNode }) => (
  <div className="rounded-lg border border-border/70 bg-muted/20 px-4 py-3">
    <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="mt-1 font-medium text-foreground">{value || "N/A"}</p>
  </div>
);

const NotesEditorContent = () => {
  const { swivuAppGrants, selectedBoard } = useContext(BryntumContext);
  const { saveNote, loading, updateNote, canEditNote, noteData, hasChanges, markAsSaved, selectedNote, wordCount, newNote, onAfterSaveNote, handleCloseEditor } = useEntityNotesContext();
  const [formIsDirty, setFormIsDirty] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  const editorContainerRef = useRef<HTMLDivElement>(null);

  const noteRecord = newNote || selectedNote.noteRecord;
  const isNewNote = Boolean(newNote) || (noteRecord?.isPhantom ?? false);
  const isSaveDisabled = (!hasChanges && !formIsDirty) || !noteRecord || loading.savingNote;
  const canEdit = isNewNote ? true : noteRecord ? canEditNote(noteRecord) : false;

  const form = useForm({
    defaultValues: {
      archived: noteRecord?.archived,
      canEdit: noteRecord?.canEdit,
      canView: noteRecord?.canView
    }
  });

  // Computed values for cleaner code
  const isFieldsDisabled = isNewNote || !canEdit || !noteRecord;

  // Centralized save logic to avoid duplication
  const saveChanges = async (formData?: Partial<BoardNote>) => {
    if (!noteRecord) return;

    if (hasChanges) {
      await saveNote(noteRecord, {
        content: noteData,
        board: isNewNote ? Number(selectedBoard?.id) : noteRecord.boardId,
        archived: isNewNote ? false : noteRecord.archived
      });
      markAsSaved();
    }

    if (form.formState.isDirty) {
      const data = formData || form.getValues();
      updateNote(noteRecord, {
        canEdit: data.canEdit,
        canView: data.canView
      });
    }
    onAfterSaveNote();
  };

  const onSubmit = async (data: Partial<BoardNote>) => {
    await saveChanges(data);
  };

  const handleClose = () => {
    // Check if there are unsaved changes
    if (hasChanges || form.formState.isDirty) {
      setShowUnsavedDialog(true);
    } else {
      setIsOpen(false);
      handleCloseEditor();
    }
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      handleClose();
    } else {
      setIsOpen(open);
    }
  };

  // Handle dialog actions
  const handleKeepEditing = () => {
    setShowUnsavedDialog(false);
  };

  const handleDontSave = () => {
    setShowUnsavedDialog(false);
    setIsOpen(false);
    handleCloseEditor();
  };

  const handleSaveAndClose = async () => {
    await saveChanges();
    setShowUnsavedDialog(false);
    setIsOpen(false);
    handleCloseEditor();
  };

  useEffect(() => {
    setFormIsDirty(form.formState.isDirty);
  }, [form.formState.isDirty, setFormIsDirty]);

  // Sync form values when noteRecord changes (especially when isPhantom changes and backend updates values)
  useEffect(() => {
    form.reset({
      archived: noteRecord?.archived,
      canEdit: noteRecord?.canEdit,
      canView: noteRecord?.canView
    });
  }, [noteRecord?.archived, noteRecord?.canEdit, noteRecord?.canView, form, newNote]);

  if (!noteRecord) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogOpenChange}>
      <SharedModal className="h-[80vh]" onInteractOutside={(e) => {
        e.preventDefault();
        handleClose();
      }} showCloseButton={false}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
          <div className="flex items-center justify-between p-4 border-b gap-2 min-w-0 flex-none">
            <div className="flex items-center gap-3 flex-1 overflow-hidden">
              <h2 className="text-xl text-muted-foreground">{newNote ? "Create new note" : canEdit ? "Edit note" : "View note"}</h2>
            </div>
            <div className="flex items-center gap-2 flex-none">


              {(loading.savingNote || loading.deletingNote || loading.duplicatingNote || loading.loadingNoteData) && (
                <p className="text-sm text-muted-foreground mr-2 whitespace-nowrap flex items-center gap-1 pr-2 border-r border-r-border/70">
                  {loading.savingNote && "Saving"}
                  {loading.deletingNote && "Deleting"}
                  {loading.duplicatingNote && "Duplicating"}
                  {loading.loadingNoteData && "Loading"}
                  {"  "} <Loader2 className="size-3 animate-spin" />
                </p>
              )}
              <p className="text-sm  text-muted-foreground mr-2 whitespace-nowrap">
                {wordCount.words} words, {wordCount.characters} characters
              </p>
              {canEdit && <Button type="submit" size="sm" className="gap-2" disabled={isSaveDisabled}>
                <Save />
                {isNewNote ? "Save Note" : "Save Changes"}
              </Button>}
              <NotesMenu />
              <Button type="button" variant="ghost" size="icon-sm" className="gap-2" onClick={handleClose}>
                <X />
                <span className="sr-only">Close</span>
              </Button>
            </div>
          </div>
          {/* Main Content Area with Embedded Sidebar */}
          <div className="flex-1 flex min-h-0 relative py-6 px-6">
            {/* Rich Text Editor */}
            <div ref={editorContainerRef} className={cn("flex-1 relative [&_.ck-content]:!border [&_.ck-content]:!border-border [&_.ck-content]:!border-solid mr-0", canEdit && "[&_.ck-content]:!border-t-0")}>
              {loading.savingNote && (
                <div className="absolute z-50 top-0 left-0 w-full h-full flex items-center justify-center bg-background/50 backdrop-blur-sm">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              )}
              <NotePreview readOnly={!canEdit} contentElementRef={editorContainerRef} />
            </div>

            {/* Embedded Settings Sidebar */}
            <div className="bg-background overflow-y-auto w-80 opacity-100">
              {/* Header */}
                  <div className="grid gap-y-10 pb-4">
                    {/* Permissions Section */}
                    <div className="space-y-3 px-4">
                      <h4 className="text-xs text-muted-foreground uppercase">Permissions</h4>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="canEdit" className="text-sm font-medium">
                            Can Edit
                          </Label>
                          <Controller
                            name="canEdit"
                            control={form.control}
                            disabled={isFieldsDisabled}
                            render={({ field }) => (
                              <Combobox
                                size="sm"
                                {...field}
                                options={swivuAppGrants}
                                value={field.value}
                                onValueChange={field.onChange}
                                placeholder="Select who can edit"
                                clearable={false}
                                disabled={isFieldsDisabled}
                                onBeforeSelect={({ value }) => {
                                  return checkGrantOrder({ canEdit: Number(value || 0), canView: form.getValues("canView") || 0, form });
                                }}
                              />
                            )}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="canView" className="text-sm font-medium">
                            Can View
                          </Label>
                          <Controller
                            name="canView"
                            control={form.control}
                            disabled={isFieldsDisabled}
                            render={({ field }) => (
                              <Combobox
                                {...field}
                                options={swivuAppGrants}
                                value={field.value}
                                onValueChange={field.onChange}
                                placeholder="Select who can view"
                                clearable={false}
                                disabled={isFieldsDisabled}
                                onBeforeSelect={({ value }) => {
                                  return checkGrantOrder({ canEdit: form.getValues("canEdit") || 0, canView: Number(value || 0) });
                                }}
                              />
                            )}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Metadata Section */}
                    <div className="space-y-3 px-4">
                      <h4 className="text-xs text-muted-foreground uppercase">Metadata</h4>
                      <div className="grid gap-3 text-sm">
                        <MetadataCard
                          label="Last Updated"
                          value={
                            <>
                              {noteRecord?.lastUpdatedBy}
                              <br />
                              {noteRecord?.lastUpdatedAt && DateHelper.format(new Date(noteRecord.lastUpdatedAt), "MM/DD/YYYY")}
                            </>
                          }
                        />
                        <MetadataCard label="Size" value={noteRecord?.formatFileSize} />
                        <MetadataCard label="Added By" value={noteRecord?.creatorName} />
                        <MetadataCard label="Age" value={String(noteRecord?.aging)} />
                      </div>
                    </div>
                  </div>
            </div>
          </div>
        </form>

        {/* Unsaved Changes Confirmation Dialog */}
        <Dialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
          <SharedModal className="md:max-w-md flex-col top-1/2 h-auto">
            <SharedModalHeader>
              <SharedModalTitle className="flex items-center gap-2 text-lg font-semibold">
                <AlertTriangle className="size-6 mr-2 text-yellow-500" /> Unsaved Changes
              </SharedModalTitle>
              <SharedModalDescription>You have unsaved changes. What would you like to do?</SharedModalDescription>
            </SharedModalHeader>
            <SharedModalBody>
              <DialogFooter className="flex-col sm:flex-row gap-3 border-t-0">
                <Button variant="ghost" onClick={handleKeepEditing} size="sm">
                  Keep working
                </Button>
                <Button variant="destructive" onClick={handleDontSave} size="sm">
                  Do not save
                </Button>
                <Button variant="success" onClick={handleSaveAndClose} size="sm">
                  Save & close
                </Button>
              </DialogFooter>
            </SharedModalBody>
          </SharedModal>
        </Dialog>
      </SharedModal>
    </Dialog>
  );
};


interface SwivuNotesEditorProps {
  record: {
    entityRecord: EntityRecord;
    noteRecord?: BoardNote;
    isCreatingNew?: boolean;
  };
  onClose: () => void;
  editorInstance?: unknown;
}

export const SwivuNotesEditor = ({ record, onClose }: SwivuNotesEditorProps) => {
  const { entityRecord, noteRecord, isCreatingNew } = record;
  return (
    <EntityNotesProvider
      entityRecord={entityRecord}
      initialNoteRecord={noteRecord}
      isCreatingNew={isCreatingNew}
      onCloseEditor={onClose}
    >
      <NotesEditorContent />
    </EntityNotesProvider>
  );
};
